import { useCurrentSite } from '@hotjar/site';
import { useCurrentUser } from '@hotjar/user';
import { useQuery } from 'react-query';
import type { UseQueryResult } from 'react-query';
import { get as getAppState, set as setAppState } from '@hotjar/webapp/domains/common/app-state';
import type { SiteId } from '@hotjar/site';
import type { UserId } from '@hotjar/user';
import { queryClient } from '@hotjar/webapp/utils/react-query';
import { useCallback } from 'react';

const NAMESPACE = 'recording_viewed_counter';
const key = 'counter';
const QUERY_KEY = NAMESPACE;

export const getCounter = async (
  siteId: SiteId | undefined,
  userId: Parameters<typeof getAppState>[0] | undefined,
): Promise<number> => {
  if (!userId || !siteId) {
    return 0;
  }
  const recordingViewedCounter = await getAppState(userId, { siteId });

  if (recordingViewedCounter[NAMESPACE] && recordingViewedCounter[NAMESPACE][key]) {
    return Number(recordingViewedCounter[NAMESPACE][key]);
  }

  return 0;
};

export const increment = async (siteId: SiteId, userId: UserId): Promise<number> => {
  if (userId && siteId) {
    const currentCounter = queryClient.getQueryData<number>([QUERY_KEY, { siteId, userId }]) || 0;
    const nextCounter = currentCounter + 1;

    const scopedKeyValue = {
      namespace: NAMESPACE,
      key,
      value: nextCounter.toString(),
    };
    const targeting = { siteId };

    await setAppState(userId, scopedKeyValue, targeting);
    queryClient.invalidateQueries([QUERY_KEY, { siteId, userId }]);
  }

  return 0;
};

type CounterValue = number;

export const useCounter = (): UseQueryResult<CounterValue | undefined, Error> => {
  const { data: currentSite } = useCurrentSite();
  const { data: user } = useCurrentUser();
  const isEnabled = !!currentSite?.id && !!user?.id;

  return useQuery(
    [QUERY_KEY, { siteId: currentSite?.id, userId: user?.id }],
    () => getCounter(currentSite?.id, user?.id),
    {
      cacheTime: 1000 * 60 * 60 * 24, // 24h
      staleTime: 1000 * 60 * 60 * 24, // 24h
      enabled: isEnabled,
    },
  );
};

interface RecordingsStatefulCounter {
  counterValue: number;
  onIncrement: () => void;
  onGetCounter: () => void;
}

export const useRecordingsStatefulCounter = (): RecordingsStatefulCounter => {
  const { data: counterValue } = useCounter();
  const { data: currentSite } = useCurrentSite();
  const { data: user } = useCurrentUser();

  const onIncrement = useCallback(async () => {
    if (currentSite?.id && user?.id) {
      await increment(currentSite?.id, user?.id);
    }
  }, [currentSite?.id, user?.id]);

  const onGetCounter = useCallback(async () => {
    if (currentSite?.id && user?.id) {
      await getCounter(currentSite?.id, user?.id);
    }
  }, [currentSite?.id, user?.id]);

  return {
    onIncrement,
    onGetCounter,
    counterValue: counterValue || 0,
  };
};
